import React from 'react';
import cx from 'classnames';

import styles from './LessonEditBlock.module.scss';

import BlockHeader, { BlockHeaderProps } from '../../molecules/BlockHeader';
import InputField, { InputFieldProps } from '../../molecules/InputField';
import TextAreaField, { TextAreaFieldProps } from '../../molecules/TextAreaField';
import InputWithStepperField, { InputWithStepperFieldProps } from '../../molecules/InputWithStepperField';
import ResourceTable, { ResourceTableProps } from '../../organisms/ResourceTable';
import { defaultProps as defaultResourceTableProps } from '../../organisms/ResourceTable/ResourceTable';
import ButtonSection, { ButtonSectionProps } from '../../molecules/ButtonSection';
import ConfirmationModal, { ConfirmationModalProps } from '../../organisms/ConfirmationModal/ConfirmationModal';

export const defaultProps = {
  blockHeader: {
    type: 'Default',
    text: {
      type: 'Title1',
      style: 'Dark',
      size: 'Medium',
      align: 'Left',
      value: 'Lesson 1',
    },
  } as BlockHeaderProps,
  name: {
    state: 'Default',
    label: {
      type: 'Paragraph1',
      style: 'Dark',
      size: 'Large',
      align: 'Left',
      value: 'Lesson name',
    },
    input: {
      style: 'Grey',
      textPlaceholder: 'Input text',
    },
  } as InputFieldProps,
  description: {
    state: 'Default',
    label: {
      type: 'Paragraph1',
      style: 'Dark',
      size: 'Large',
      align: 'Left',
      value: 'Lesson description',
    },
    textArea: {
      textPlaceholder: 'Text area text',
    },
  } as TextAreaFieldProps,
  estimatedTime: {
    state: 'Default',
    label: {
      type: 'Paragraph1',
      style: 'Dark',
      size: 'Large',
      align: 'Left',
      value: 'Estimated time (In minutes)',
    },
    inputWithStepper: {
      input: {
        style: 'Grey',
        textPlaceholder: '00',
      },
      stepDown: {
        type: 'IconButton',
        size: 'Small',
        fill: 'White',
        colour: 'Basic',
        icon: {
          asset: 'ChevronUp',
          style: 'Disabled',
        },
      },
      stepUp: {
        type: 'IconButton',
        size: 'Small',
        fill: 'White',
        colour: 'Basic',
        icon: {
          asset: 'ChevronDown',
          style: 'Disabled',
        },
      },
    },
  } as InputWithStepperFieldProps,
  resourceTable: {
    ...defaultResourceTableProps
  } as ResourceTableProps,
  buttonSection: {
    state: 'WithDeleteButton',
    divider: {
      style: 'Horizontal',
    },
    buttonGroup: {
      primary: {
        type: 'TextButton',
        size: 'Large',
        fill: 'Colour',
        colour: 'Brand',
        text: {
          type: 'Subtitle1',
          style: 'White',
          size: 'Medium',
          align: 'Center',
          value: 'Save',
        },
      },
      secondary: {
        type: 'TextButton',
        size: 'Large',
        fill: 'White',
        colour: 'Brand',
        text: {
          type: 'Subtitle1',
          style: 'Brand',
          size: 'Medium',
          align: 'Center',
          value: 'Discard',
        },
      },
    },
    tertiary: {
      type: 'TextButton',
      size: 'Large',
      fill: 'White',
      colour: 'Danger',
      text: {
        type: 'Subtitle1',
        style: 'Brand',
        size: 'Medium',
        align: 'Center',
        value: 'Delete',
      },
    },
  } as ButtonSectionProps,
};

export type LessonEditBlockProps = {
  blockHeader?: BlockHeaderProps;
  name?: InputFieldProps;
  description?: TextAreaFieldProps;
  estimatedTime?: InputWithStepperFieldProps;
  resourceTable?: ResourceTableProps;
  buttonSection?: ButtonSectionProps;
  className?: string;
};

const LessonEditBlock: React.FC<LessonEditBlockProps> = ({
  blockHeader,
  name,
  description,
  estimatedTime,
  resourceTable,
  buttonSection,
  className,
}) => {
  return (
    <div className={cx(styles.lessonEditBlock, className)}>
      <BlockHeader
        className={styles.blockHeader}
        {...blockHeader} />
      <div className={styles.card}>
        <InputField
          className={styles.name}
          {...name} />
        <TextAreaField
          className={styles.description}
          {...description} />
        <InputWithStepperField
          className={styles.estimatedTime}
          {...estimatedTime} />
        <ResourceTable
          className={styles.resourceTable}
          {...resourceTable} />
        <ButtonSection
          className={styles.buttonSection}
          {...buttonSection} />
      </div>
    </div>
  );
};

LessonEditBlock.defaultProps = defaultProps;

export default LessonEditBlock;
